/**
 * Scheduling v2 — arrival-window presets (docs/scheduling/scheduling-v2-architecture.md §4.2).
 *
 * A preset is "Morning, 8:00–10:00, Mon–Fri, all business units, max 6 visits".
 * Delete archives (`isActive: false` from the server's point of view); the list
 * is fetched with `includeInactive` so the settings grid can show and restore
 * archived rows, while booking surfaces read the active set only.
 */
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiDelete, apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api/client';
import { schedulingV2Endpoints } from '@/lib/api/endpoints';
import { schedulingV2Keys } from '@/lib/queryKeys';
import type {
  ArrivalWindowPresetListParams,
  ArrivalWindowPresetListResponse,
  ArrivalWindowPresetView,
  CreateArrivalWindowPresetInput,
  ReorderArrivalWindowPresetsInput,
  UpdateArrivalWindowPresetInput,
} from '@/types/schedulingV2';

/** Preserve an explicit all-unit scope while reading older single-unit responses. */
export function arrivalWindowBusinessUnitIds(preset: ArrivalWindowPresetView): string[] {
  return preset.businessUnitIds ?? (preset.businessUnitId ? [preset.businessUnitId] : []);
}

/** Rows follow `sortOrder`; ties break on creation so a new preset lands last. */
export function sortArrivalWindowPresets(
  presets: readonly ArrivalWindowPresetView[],
): ArrivalWindowPresetView[] {
  return presets.toSorted(
    (a, b) => a.sortOrder - b.sortOrder || a.createdAt.localeCompare(b.createdAt),
  );
}

export function useArrivalWindowPresets(params: ArrivalWindowPresetListParams = {}) {
  const { effective, ...queryParams } = params;
  return useApiQuery<ArrivalWindowPresetListResponse, ArrivalWindowPresetView[]>({
    queryKey: schedulingV2Keys.arrivalWindows(params),
    queryFn: ({ signal }) =>
      apiGet<ArrivalWindowPresetListResponse>(
        operationsClient,
        effective
          ? schedulingV2Endpoints.effectiveArrivalWindows()
          : schedulingV2Endpoints.arrivalWindows(),
        { params: queryParams, signal },
      ),
    select: (res) => sortArrivalWindowPresets(res.items),
  });
}

function useInvalidatePresets() {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({ queryKey: schedulingV2Keys.arrivalWindowLists() });
}

export function useCreateArrivalWindowPreset() {
  const invalidate = useInvalidatePresets();
  return useApiMutation<ArrivalWindowPresetView, CreateArrivalWindowPresetInput>({
    mutationFn: (payload) =>
      apiPost<ArrivalWindowPresetView, CreateArrivalWindowPresetInput>(
        operationsClient,
        schedulingV2Endpoints.arrivalWindows(),
        payload,
      ),
    // The editor maps a 409 (same times, same unit) onto its own field, so the
    // generic toast would double up on the one error the form already shows.
    showErrorToast: false,
    onSuccess: () => toast.success('Arrival window added'),
    onSettled: invalidate,
  });
}

export function useUpdateArrivalWindowPreset() {
  const invalidate = useInvalidatePresets();
  return useApiMutation<
    ArrivalWindowPresetView,
    { id: string; payload: UpdateArrivalWindowPresetInput; successMessage?: string | null }
  >({
    mutationFn: ({ id, payload }) =>
      apiPatch<ArrivalWindowPresetView, UpdateArrivalWindowPresetInput>(
        operationsClient,
        schedulingV2Endpoints.arrivalWindow(id),
        payload,
      ),
    showErrorToast: false,
    onSuccess: (_preset, { successMessage }) => {
      if (successMessage !== null) toast.success(successMessage ?? 'Arrival window saved');
    },
    onError: (error) => toast.error(error.message),
    onSettled: invalidate,
  });
}

/** `DELETE` archives the preset; existing visits keep their promised times. */
export function useArchiveArrivalWindowPreset() {
  const invalidate = useInvalidatePresets();
  return useApiMutation<ArrivalWindowPresetView, string>({
    mutationFn: (id) =>
      apiDelete<ArrivalWindowPresetView>(operationsClient, schedulingV2Endpoints.arrivalWindow(id)),
    onSuccess: () => toast.success('Arrival window deleted'),
    onSettled: invalidate,
  });
}

/**
 * Persist a new display order in one request. The server rewrites `sortOrder`
 * for every id in `orderedIds` and returns the reordered list, which replaces
 * the cached rows so Move up / Move down settle without a refetch flash.
 */
export function useReorderArrivalWindowPresets() {
  const queryClient = useQueryClient();
  return useApiMutation<ArrivalWindowPresetListResponse, ReorderArrivalWindowPresetsInput>({
    mutationFn: (payload) =>
      apiPost<ArrivalWindowPresetListResponse, ReorderArrivalWindowPresetsInput>(
        operationsClient,
        schedulingV2Endpoints.arrivalWindowsReorder(),
        payload,
      ),
    onSuccess: (res) => {
      queryClient.setQueriesData<ArrivalWindowPresetListResponse>(
        { queryKey: schedulingV2Keys.arrivalWindowLists() },
        (current) => {
          if (!current) return current;
          const byId = new Map(res.items.map((item) => [item.id, item]));
          return { items: current.items.map((item) => byId.get(item.id) ?? item) };
        },
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: schedulingV2Keys.arrivalWindowLists() });
    },
  });
}
